import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { errMsg } from "@/lib/api";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { DateInput } from "@/components/common/DateInput";

const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function toForm(fields, initial) {
  const v = {};
  fields.forEach((f) => {
    const val = initial?.[f.name];
    if (f.type === "tags") v[f.name] = Array.isArray(val) ? val.join(", ") : val || "";
    else if (f.type === "checkbox") v[f.name] = Boolean(val ?? f.default);
    else if (f.type === "multiselect") v[f.name] = val || [];
    else v[f.name] = val ?? f.default ?? "";
  });
  return v;
}

function fromForm(fields, values) {
  const out = {};
  fields.forEach((f) => {
    const val = values[f.name];
    if (f.type === "number") out[f.name] = val === "" || val == null ? null : Number(val);
    else if (f.type === "tags") out[f.name] = String(val || "").split(/[,\n]/).map((s) => s.trim()).filter(Boolean);
    else if (f.type === "checkbox" || f.type === "multiselect") out[f.name] = val;
    else out[f.name] = val === "" ? null : val;
  });
  return out;
}

function validate(fields, values) {
  const errs = {};
  fields.forEach((f) => {
    const val = values[f.name];
    const empty = val === "" || val == null || (Array.isArray(val) && !val.length);
    if (f.required && empty) errs[f.name] = "Campo obligatorio";
    else if (!empty && f.type === "email" && !EMAIL_RX.test(val)) errs[f.name] = "Correo no válido";
    else if (!empty && f.type === "number" && (isNaN(Number(val)) || (f.min != null && Number(val) < f.min))) errs[f.name] = `Debe ser un número ≥ ${f.min ?? 0}`;
    else if (!empty && f.minLength && String(val).length < f.minLength) errs[f.name] = `Mínimo ${f.minLength} caracteres`;
  });
  return errs;
}

export function FieldInput({ field: f, value, onChange, error, testid }) {
  const id = `${testid}-${f.name}`;
  const common = { id, "data-testid": id, className: "field-input", disabled: f.disabled, placeholder: f.placeholder, "aria-invalid": Boolean(error) };
  let input;
  if (f.type === "textarea" || f.type === "tags") input = <textarea rows={f.rows || 3} {...common} value={value} onChange={(e) => onChange(e.target.value)} />;
  else if (f.type === "select")
    input = (
      <select {...common} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">{f.placeholder || "Seleccione…"}</option>
        {(f.options || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    );
  else if (f.type === "date") input = <DateInput id={id} testid={id} value={value} onChange={onChange} disabled={f.disabled} />;
  else if (f.type === "checkbox")
    return (
      <label className="flex items-center gap-2 text-sm text-slate-700 sm:col-span-2" htmlFor={id}>
        <input type="checkbox" id={id} data-testid={id} checked={value} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4 accent-blue-700" />
        {f.label}
      </label>
    );
  else if (f.type === "multiselect")
    input = (
      <div className="flex flex-wrap gap-2 rounded-md border border-input bg-white p-2" data-testid={id}>
        {(f.options || []).length === 0 && <span className="text-xs text-slate-400">Sin opciones</span>}
        {(f.options || []).map((o) => (
          <label key={o.value} className="flex items-center gap-1.5 rounded border border-slate-200 px-2 py-1 text-xs">
            <input type="checkbox" checked={value.includes(o.value)} onChange={(e) => onChange(e.target.checked ? [...value, o.value] : value.filter((x) => x !== o.value))} className="accent-blue-700" />
            {o.label}
          </label>
        ))}
      </div>
    );
  else input = <input type={f.type === "number" ? "number" : f.type || "text"} step={f.step || "any"} {...common} value={value} onChange={(e) => onChange(e.target.value)} />;
  return (
    <div className={f.full || ["textarea", "tags", "multiselect"].includes(f.type) ? "sm:col-span-2" : ""}>
      <label htmlFor={id} className="field-label">{f.label}{f.required && <span className="text-rose-500"> *</span>}</label>
      {input}
      {f.help && !error && <p className="mt-1 text-xs text-slate-400">{f.help}</p>}
      {error && <p className="mt-1 text-xs text-rose-600" data-testid={`${id}-error`}>{error}</p>}
    </div>
  );
}

export function FormDialog({ open, onOpenChange, title, description, fields, initial, onSubmit, submitLabel = "Guardar", testid = "form-dialog", wide }) {
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState("");
  const [confirmClose, setConfirmClose] = useState(false);
  const baseline = useRef("{}");

  useEffect(() => {
    if (open) {
      const v = toForm(fields, initial || {});
      setValues(v);
      baseline.current = JSON.stringify(v);
      setErrors({});
      setServerError("");
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const dirty = JSON.stringify(values) !== baseline.current;
  const requestClose = () => {
    if (saving) return;
    if (dirty) setConfirmClose(true);
    else onOpenChange(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate(fields, values);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSaving(true);
    setServerError("");
    try {
      await onSubmit(fromForm(fields, values));
      baseline.current = JSON.stringify(values);
      onOpenChange(false);
    } catch (err) {
      setServerError(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : requestClose())}>
        <DialogContent className={`${wide ? "sm:max-w-3xl" : "sm:max-w-2xl"} max-h-[92vh] overflow-y-auto`} data-testid={testid}>
          <DialogHeader>
            <DialogTitle className="font-display">{title}</DialogTitle>
            {description && <DialogDescription>{description}</DialogDescription>}
          </DialogHeader>
          <form onSubmit={submit} noValidate className="space-y-5">
            <div className="grid gap-4 sm:grid-cols-2">
              {fields.map((f) => (
                <FieldInput key={f.name} field={f} value={values[f.name] ?? ""} error={errors[f.name]} testid={testid}
                  onChange={(v) => setValues((s) => ({ ...s, [f.name]: v }))} />
              ))}
            </div>
            {serverError && <p role="alert" className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700" data-testid={`${testid}-error`}>{serverError}</p>}
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={requestClose} disabled={saving} data-testid={`${testid}-cancel`}>Cancelar</Button>
              <Button type="submit" disabled={saving} data-testid={`${testid}-save`}>
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {submitLabel}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <ConfirmDialog open={confirmClose} onOpenChange={setConfirmClose} title="Hay cambios sin guardar"
        description="Si cierra esta ventana perderá la información que escribió." confirmLabel="Descartar cambios" destructive
        testid={`${testid}-unsaved`} onConfirm={async () => { setConfirmClose(false); onOpenChange(false); }} />
    </>
  );
}
